/**
 * Regras de negócio puras para validação de datas e conclusão de tarefas/subtarefas.
 *
 * Centraliza a lógica usada tanto na criação (rascunhos) quanto na edição
 * (subtarefas persistidas) e no fluxo de conclusão, evitando duplicação entre
 * componentes. As datas seguem o formato `YYYY-MM-DD`, que é comparável
 * lexicograficamente de forma segura.
 */

export const SUBTASK_AFTER_PARENT_MESSAGE =
  "A data da subtarefa não pode ser posterior à data da tarefa principal.";

export const SUBTASK_BEFORE_PREVIOUS_MESSAGE =
  "A data desta subtarefa não pode ser anterior à da subtarefa anterior.";

export const SUBTASK_AFTER_NEXT_MESSAGE =
  "A data desta subtarefa não pode ser posterior à da próxima subtarefa.";

export const PARENT_BEFORE_SUBTASK_MESSAGE =
  "A data da tarefa principal não pode ser anterior à data de uma subtarefa.";

export const COMPLETE_WITH_PENDING_SUBTASKS_MESSAGE =
  "Conclua todas as subtarefas antes de concluir a tarefa principal.";

/** Normaliza datas vazias (`""`) para `null` mantendo o restante intacto. */
function normalize(date: string | null | undefined): string | null {
  return date ? date : null;
}

/**
 * Regra 1: a subtarefa não pode ter data posterior à da tarefa-pai.
 * - Pai sem data ⇒ subtarefa livre.
 * - Subtarefa sem data ⇒ sempre válida.
 */
export function subtaskDateExceedsParent(
  parentDueDate: string | null | undefined,
  subtaskDueDate: string | null | undefined,
): boolean {
  const parent = normalize(parentDueDate);
  const subtask = normalize(subtaskDueDate);
  if (!parent || !subtask) return false;
  return subtask > parent;
}

export interface SubtaskDateValidationInput {
  parentDueDate: string | null | undefined;
  subtaskDueDate: string | null | undefined;
  /** Data da subtarefa imediatamente anterior na ordem (se existir e tiver data). */
  previousSiblingDueDate?: string | null;
  /** Data da próxima subtarefa na ordem (se existir e tiver data). */
  nextSiblingDueDate?: string | null;
}

/**
 * Valida a data de uma única subtarefa contra o pai e seus vizinhos cronológicos.
 * Retorna a primeira mensagem de erro encontrada ou `null` quando válida.
 */
export function getSubtaskDateError({
  parentDueDate,
  subtaskDueDate,
  previousSiblingDueDate,
  nextSiblingDueDate,
}: SubtaskDateValidationInput): string | null {
  const subtask = normalize(subtaskDueDate);
  if (!subtask) return null;

  if (subtaskDateExceedsParent(parentDueDate, subtask)) {
    return SUBTASK_AFTER_PARENT_MESSAGE;
  }

  const previous = normalize(previousSiblingDueDate);
  if (previous && subtask < previous) {
    return SUBTASK_BEFORE_PREVIOUS_MESSAGE;
  }

  const next = normalize(nextSiblingDueDate);
  if (next && subtask > next) {
    return SUBTASK_AFTER_NEXT_MESSAGE;
  }

  return null;
}

export interface DatedSubtaskLike {
  due_date: string | null;
}

export interface SubtaskListValidationError {
  index: number;
  message: string;
}

/**
 * Valida uma lista ordenada de subtarefas contra a data do pai e a ordem
 * cronológica entre elas. Subtarefas sem data são ignoradas (permitidas).
 * Retorna o índice e a mensagem do primeiro erro, ou `null` quando tudo válido.
 */
export function validateSubtaskList(
  parentDueDate: string | null | undefined,
  items: DatedSubtaskLike[],
): SubtaskListValidationError | null {
  let lastDated: string | null = null;

  for (let index = 0; index < items.length; index += 1) {
    const due = normalize(items[index].due_date);
    if (!due) continue;

    if (subtaskDateExceedsParent(parentDueDate, due)) {
      return { index, message: SUBTASK_AFTER_PARENT_MESSAGE };
    }

    if (lastDated && due < lastDated) {
      return { index, message: SUBTASK_BEFORE_PREVIOUS_MESSAGE };
    }

    lastDated = due;
  }

  return null;
}

/**
 * Regra 2: a tarefa-pai só pode ser concluída quando todas as subtarefas
 * (não canceladas) estiverem concluídas. Sem subtarefas ⇒ liberada.
 */
export function allSubtasksCompleted(progress: {
  total: number;
  done: number;
}): boolean {
  return progress.total === 0 || progress.done >= progress.total;
}
